import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase.config';

export interface UserData {
    uid: string;
    email: string;
    fullName: string;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    notifyOnTouristVisa?: boolean;
    notifyOnGlobalTalentVisa?: boolean;
    profileAnalysisCount?: number;
    lastProfileAnalysisDate?: string; // YYYY-MM-DD format
}

export const createUserDocument = async (uid: string, email: string, fullName: string): Promise<void> => {
    try {
        await setDoc(doc(db, 'users', uid), {
            uid,
            email,
            fullName,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error creating user document:', error);
        throw error;
    }
};

export const getUserDocument = async (uid: string): Promise<UserData | null> => {
    try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
            return userDoc.data() as UserData;
        }
        return null;
    } catch (error) {
        console.error('Error fetching user document:', error);
        throw error;
    }
};

export const updateUserDocument = async (uid: string, data: Partial<UserData>): Promise<void> => {
    try {
        await updateDoc(doc(db, 'users', uid), {
            ...data,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating user document:', error);
        throw error;
    }
};

export const updateTouristVisaNotification = async (uid: string, notifyOnTouristVisa: boolean): Promise<void> => {
    try {
        await updateDoc(doc(db, 'users', uid), {
            notifyOnTouristVisa,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating tourist visa notification preference:', error);
        throw error;
    }
};

export const updateGlobalTalentVisaNotification = async (uid: string, notifyOnGlobalTalentVisa: boolean): Promise<void> => {
    try {
        await updateDoc(doc(db, 'users', uid), {
            notifyOnGlobalTalentVisa,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating global talent visa notification preference:', error);
        throw error;
    }
};

export const checkProfileAnalysisLimit = async (uid: string): Promise<{ canAnalyze: boolean; remainingCount: number; resetDate: string }> => {
    try {
        const userData = await getUserDocument(uid);
        if (!userData) {
            throw new Error('User document not found');
        }

        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const lastAnalysisDate = userData.lastProfileAnalysisDate;
        const currentCount = userData.profileAnalysisCount || 0;

        // Reset count if it's a new day
        if (lastAnalysisDate !== today) {
            return {
                canAnalyze: true,
                remainingCount: 3,
                resetDate: today
            };
        }

        // Check if user has remaining analyses for today
        const remainingCount = Math.max(0, 3 - currentCount);
        const canAnalyze = remainingCount > 0;

        return {
            canAnalyze,
            remainingCount,
            resetDate: today
        };
    } catch (error) {
        console.error('Error checking profile analysis limit:', error);
        throw error;
    }
};

export const incrementProfileAnalysisCount = async (uid: string): Promise<void> => {
    try {
        const userData = await getUserDocument(uid);
        if (!userData) {
            throw new Error('User document not found');
        }

        const today = new Date().toISOString().split('T')[0];
        const lastAnalysisDate = userData.lastProfileAnalysisDate;
        const currentCount = userData.profileAnalysisCount || 0;

        // Reset count if it's a new day, otherwise increment
        const newCount = lastAnalysisDate === today ? currentCount + 1 : 1;

        await updateDoc(doc(db, 'users', uid), {
            profileAnalysisCount: newCount,
            lastProfileAnalysisDate: today,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error incrementing profile analysis count:', error);
        throw error;
    }
}; 